import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { downscaleImage } from '../lib/downscale-image.js';

// Reference photos in the task editor (CJ 2026-09-30): "upload a picture with
// the task or several if there are steps ... what the final outcome should be."
//
// Photos already on the task (editing) are changed immediately — caption on
// blur, remove on tap. NEW photos are staged here and uploaded by the parent
// after the task saves, because a brand-new task has no id to attach them to.
export const MAX_TASK_PHOTOS = 8;

const input = 'w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30';

export function TaskPhotosEditor({ taskId, staged, onStagedChange }) {
  const [existing, setExisting] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!taskId) return;
    api('GET', `/api/task-photos?task_id=${taskId}`)
      .then((r) => setExisting(r?.photos || []))
      .catch((e) => setErr(e?.message || 'Could not load photos'));
  }, [taskId]);

  async function addFiles(files) {
    const room = MAX_TASK_PHOTOS - existing.length - staged.length;
    const list = [...files].slice(0, Math.max(0, room));
    if (!list.length) { setErr(`A task can have up to ${MAX_TASK_PHOTOS} photos.`); return; }
    setBusy(true); setErr('');
    try {
      const shrunk = await Promise.all(list.map(downscaleImage));
      onStagedChange([...staged, ...shrunk.map((p) => ({ ...p, caption: '', key: `${Date.now()}-${Math.random()}` }))]);
    } catch (e) { setErr(`Couldn't read that photo: ${e?.message || e}`); }
    finally { setBusy(false); }
  }

  async function saveCaption(p, caption) {
    if ((p.caption || '') === caption) return;
    try {
      await api('PATCH', `/api/task-photos/${p.id}`, { caption });
      setExisting((xs) => xs.map((x) => (x.id === p.id ? { ...x, caption } : x)));
    } catch (e) { setErr(e?.message || 'Could not save that caption'); }
  }

  async function removeExisting(p) {
    try {
      await api('DELETE', `/api/task-photos/${p.id}`);
      setExisting((xs) => xs.filter((x) => x.id !== p.id));
    } catch (e) { setErr(e?.message || 'Could not remove that photo'); }
  }

  const total = existing.length + staged.length;
  const tile = (src, key, caption, onCaption, onBlur, onRemove, n) => (
    <div key={key} className="flex gap-2 items-start">
      <span className="text-xs font-bold text-gray-400 w-4 pt-2">{n}</span>
      <img src={src} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0" />
      <input value={caption} onChange={(e) => onCaption(e.target.value)} onBlur={onBlur} maxLength={200}
        className={input} placeholder="Caption — e.g. “Step 1: empty the hopper” or “Finished”" />
      <button type="button" onClick={onRemove} aria-label="Remove photo"
        className="text-gray-400 hover:text-red-500 text-lg leading-none px-1 pt-1">×</button>
    </div>
  );

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Reference photos</label>
      <p className="text-xs text-gray-500 mb-2">
        Show the steps, or what it should look like when it's done. Staff see these on the task and the store iPad.
      </p>
      <div className="space-y-2">
        {existing.map((p, i) => tile(
          p.url, `e${p.id}`, p._draft ?? p.caption ?? '',
          (v) => setExisting((xs) => xs.map((x) => (x.id === p.id ? { ...x, _draft: v } : x))),
          () => saveCaption(p, (p._draft ?? p.caption ?? '').trim()),
          () => removeExisting(p), i + 1,
        ))}
        {staged.map((p, i) => tile(
          p.base64, p.key, p.caption,
          (v) => onStagedChange(staged.map((x) => (x.key === p.key ? { ...x, caption: v } : x))),
          undefined,
          () => onStagedChange(staged.filter((x) => x.key !== p.key)), existing.length + i + 1,
        ))}
      </div>
      {total < MAX_TASK_PHOTOS && (
        <label className={`mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-xs font-semibold text-gray-600 cursor-pointer hover:border-gray-400 ${busy ? 'opacity-50' : ''}`}>
          📷 {busy ? 'Preparing…' : total ? 'Add another photo' : 'Add photos'}
          <input type="file" accept="image/*" multiple className="hidden" disabled={busy}
            onChange={(e) => { addFiles(e.target.files || []); e.target.value = ''; }} />
        </label>
      )}
      {staged.length > 0 && <p className="text-xs text-gray-400 mt-1">{staged.length} new photo{staged.length === 1 ? '' : 's'} will upload when you save.</p>}
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </div>
  );
}
